import React, { useEffect, useRef, useState } from "react";

import GameCanvas from "../../game-canvas/GameCanvas";
import { createRemoteGame } from "../../online/remote-game";
import { peekPlayNow, watchRoom } from "../../online/net";
import type { RoundSetup } from "../../online/dynamic";

// What the live window is showing: the room's current round (null while it shows
// the fallback, i.e. no room is running).
export interface LiveRoomPlayer { id: string; name: string; flag: string | null; colorId: string }
export interface LiveRoomInfo {
  room: string;
  setup: RoundSetup | null;
  // who is playing this round, and who leads the set so far (null before anyone
  // has a point)
  players: LiveRoomPlayer[];
  leader: LiveRoomPlayer | null;
}

interface Props {
  // Drawn while there is no room to watch (a local, simulated game): the window
  // never stands empty.
  fallback: React.ReactNode;
  // GameCanvas's size config (as GamePreview takes it).
  size?: any;
  width?: number;
  height?: number;
  onInfo?: (info: LiveRoomInfo | null) => void;
  // false pauses it (the page is covered, or the tab is hidden): the socket is
  // dropped and picked up again when true.
  active?: boolean;
}

const PEEK_EVERY_MS = 12000; // how often to look for a room while there is none

// The start page's window into Play Now: the very room you would join, live.
// It asks the matchmaker which room that is (without joining), then watches it
// over a seatless connection and draws it like any online game. When nobody is
// playing there is no room: the fallback shows instead, and we look again every
// few seconds.
export default function LiveRoomPreview({ fallback, size, width, height, onInfo, active = true }: Props) {
  const remoteRef = useRef<ReturnType<typeof createRemoteGame> | null>(null);
  const configRef = useRef<any>(null);
  const [live, setLive] = useState(false);
  const [, setTick] = useState(0);
  const onInfoRef = useRef(onInfo);
  onInfoRef.current = onInfo;

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let socket: ReturnType<typeof watchRoom> | null = null;
    let peekTimer: ReturnType<typeof setTimeout> | null = null;

    const drop = () => {
      if (socket) { socket.removeAllListeners(); socket.disconnect(); socket = null; }
      remoteRef.current = null;
      configRef.current = null;
      setLive(false);
      onInfoRef.current?.(null);
    };
    const lookLater = () => {
      if (stopped) return;
      if (peekTimer) clearTimeout(peekTimer);
      peekTimer = setTimeout(look, PEEK_EVERY_MS);
    };
    async function look() {
      if (stopped || document.hidden) { lookLater(); return; }
      const found = await peekPlayNow();
      if (stopped) return;
      if (!found) { lookLater(); return; }
      const remote = createRemoteGame();
      let setup: RoundSetup | null = null;
      let score: Record<string, number> = {};
      const report = () => {
        const players: LiveRoomPlayer[] = (configRef.current?.players || []).map((p: any) => ({ id: p.id, name: p.name, flag: p.flag ?? null, colorId: p.colorId }));
        const top = players.slice().sort((a, b) => (score[b.id] || 0) - (score[a.id] || 0))[0];
        onInfoRef.current?.({ room: found.room, setup, players, leader: top && (score[top.id] || 0) > 0 ? top : null });
      };
      socket = watchRoom(found.room, found.origin);
      socket.on("match_start", (m: any) => { configRef.current = m.matchConfig; });
      socket.on("round_setup", (m: any) => { configRef.current = m.matchConfig; setup = m; if (m.scoreReset) score = {}; report(); });
      socket.on("game_over", (m: any) => { if (m && m.score) { score = m.score; report(); } });
      socket.on("game_start", (m: any) => {
        if (m && m.score) score = m.score;
        remote.setInitial(m.gameState);
        remoteRef.current = remote;
        setLive(true);
        setTick((t) => t + 1); // a new round: a new gameState object to draw
        report();
      });
      socket.on("game_update", (m: any) => { if (remoteRef.current) remote.applyUpdate(m.data); });
      // Nothing (left) to watch, or the connection went: back to the fallback, look again later.
      const gone = () => { drop(); lookLater(); };
      socket.on("room_closed", gone);
      socket.on("match_over", gone);
      socket.on("disconnect", gone);
    }
    const onVisibility = () => {
      if (document.hidden) { drop(); if (peekTimer) clearTimeout(peekTimer); }
      else if (!socket) look();
    };
    document.addEventListener("visibilitychange", onVisibility);
    look();
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (peekTimer) clearTimeout(peekTimer);
      drop();
    };
  }, [active]);

  const remote = remoteRef.current;
  const gameState = remote ? remote.getGameState() : null;
  if (!live || !remote || !gameState || !configRef.current) return <>{fallback}</>;
  return (
    <GameCanvas
      // spectator: nobody on this board is ours, so no worm gets the "this one is
      // yours" pulse at the round's start (without it every seat counted as ours)
      config={{ size, width, height, preview: true, spectator: true }}
      gameState={gameState}
      players={configRef.current.players}
      renderTime={() => remote.getRenderTime()}
      serverTime={remote.getServerTime}
      adaptiveDelay
    />
  );
}
